import {getEntityHistory, makeSecureApiCall} from './api';
import {format} from "date-fns";
import {getContactFromUserId} from "../lib/picos/getContactFromUser";
import {formatFML} from "../Contacts/ContactFormat";
import {
    EChargebackMessageType,
    ETransactionStatus,
} from "../@types/payrix";

import type {
	ApplicationSubmissionResults,
	BankAccounts,
	ChargebackReturn,
	PayoutDetails,
	PayrixApplicationStatus,
	PayrixApplicationStatusInfo,
	PayrixStoredApplicationStatus,
	PayrixChargebackMessage,
	PayrixCustomer,
	PayrixEntryEnhanced,
	PayrixFeeSummary,
	PayrixFunds,
	PayrixMerchant,
	PayrixPaymentMethod,
	PayrixPayout,
	PayrixPayoutWithDisbursement,
	PayrixToken,
	PayrixTransaction,
	PayrixTransactionFull, PayrixTransactionWithPayment, PayrixTxnResult,
} from "../@types/payrix";
import type {CPayrixApplication} from "../Configuration/PaymentProcessors/Payrix/Application/payrixApplication";
import Contact from "../entities/contact";
import Transaction from "../entities/transaction";
import type {PaymentMethod} from "../@types/payments";
import {Err, Ok, Result} from "oxide.ts";
import {HttpError, httpErrorMessage} from "../lib/picos/http";
import type {uuid} from "../@types/entity";
import {tabSession} from '@/Auth/session';
import {
	chargeNotSent,
	chargeOutcomeUnknown,
	PaymentNotSentError,
	PaymentOutcomeUnknownError,
	PortalChargeOperatorError,
	PortalChargeRefusedError,
} from '@/Financial/PaymentProcessor/paymentOutcome';

/**
 * @enum {number}
 * @readonly
 */
export const DisbursementStatus = {
	Requested: 1,
	Processing: 2,
	Processed: 3,
	Failed: 4,
	Denied: 5,
	Returned: 6
}

/**
 * Converts a numeric transaction status to its corresponding string representation.
 *
 * @param {number} status - The numeric status code of the transaction.
 * @returns {string} A string representation of the transaction status.
 *
 * @example
 * const statusString = getTransactionStatus(TransactionStatus.Approved);
 * console.log(statusString); // Output: "Approved"
 */
export function getTransactionStatus(status: ETransactionStatus): string {
	switch (status) {
		case ETransactionStatus.Pending:
			return 'Pending';
		case ETransactionStatus.Approved:
			return 'Approved';
		case ETransactionStatus.Failed:
			return 'Failed';
		case ETransactionStatus.Captured:
			return 'Captured';
		case ETransactionStatus.Settled:
			return 'Settled';
		case ETransactionStatus.Returned:
			return 'Returned';
		default:
			return `Unknown Status ${status}`;
	}
}

/**
 * Converts a chargeback message type to its corresponding human-readable string representation.
 *
 * @param {ChargebackMessageType} type - The type of chargeback message.
 * @returns {string | undefined} A string representation of the chargeback message type, or undefined if the type is not recognized.
 *
 * @example
 * const messageType = getChargebackMessageType(ChargebackMessageType.Assign);
 * console.log(messageType); // Output: "Assign"
 */
export function getChargebackMessageType(type: string): string | undefined {
	switch (type) {
		case EChargebackMessageType.Assign:
			return 'Assign';
		case EChargebackMessageType.Notate:
			return 'Notate';
		case EChargebackMessageType.AcceptLiability:
			return 'Accept liability';
		case EChargebackMessageType.CreatePreArbitration:
			return 'Create pre-arbitration';
		case EChargebackMessageType.Represent:
			return 'Represent';
		case EChargebackMessageType.Respond:
			return 'Respond';
		case EChargebackMessageType.RequestArbitration:
			return 'Request arbitration';
		case EChargebackMessageType.CreateArbitration:
			return 'Create arbitration';
		case EChargebackMessageType.RequestPreArbitration:
			return 'Request pre-arbitration';
		case EChargebackMessageType.RequestResolutionToPreArbitration:
			return 'Request resolution to pre-arbitration';
		case EChargebackMessageType.RespondToDispute:
			return 'Respond to dispute';
		case EChargebackMessageType.RespondToPreArbitration:
			return 'Respond to pre-arbitration';
		case EChargebackMessageType.Unaccept:
			return 'Un-accept';
		case EChargebackMessageType.FilePreArbitration:
			return 'File pre-arbitration';
		default:
			return undefined;
	}
}


/**
 * Format payrix numeric/string date into a string or new Date
 * @param {string|number} date
 * @param {boolean} asString - convert to a string in YYYY-MM-DD format
 * @returns {Date|string}
 */
export function parsePayrixDate(date: string | number, asString: boolean): Date | string {
	if (!date) return null;
	const sDate = `${date}`
	const converted = `${sDate.substring(0, 4)}-${sDate.substring(4, 6)}-${sDate.substring(6, 8)}`;
	if (asString) return converted;
	else return new Date(converted);
}

export function parsePayrixDateAsString(date: string | number): string {
	return parsePayrixDate(date, true) as string;
}

/**
 * A failed payments call (OWA-1454): its HTTP status (0 when there was no answer), and the
 * machine-readable code the body carried, if any, kept for callers that act on them.
 */
export class PaymentsCallError extends Error {
	readonly status: number;
	readonly code?: string;

	constructor(status: number, message: string, code?: string) {
		super(message);
		this.name = 'PaymentsCallError';
		this.status = status;
		this.code = code;
	}
}

/**
 * Helper function to process errors from api calls.
 * @param {{status: number, body: any}} result
 * @param {number} result.status
 * @param {any} result.body
 * @returns {any}
 */
function processResults<T>(result: {status: number, body: T}): Result<T, Error> {
	if (!result) {
		// Probably because the server is down
		return Err(new PaymentsCallError(0, '💳 Server is unavailable, try again in a few minutes'));
	}

	const {status, body} = result;

	if (status >= 400) {
		// Handle HTTP error, including object results
		let message: string;
		let b: any = body;
		const code = b instanceof Object && typeof b.code === 'string' ? b.code : undefined;
		if (b instanceof Object && b.clientMessage) message = b.clientMessage;
		else if (b instanceof Object && b.message) message = b.message;
		else b = JSON.stringify(body);

		if (status > 501 && status < 505) return Err(new PaymentsCallError(status, `The payment server is currently unavailable. Please try again later.`, code));
		return Err(new PaymentsCallError(status, `💳 Error ${status}: ${message || b}`, code));
	}
	else {
		return Ok(body);
	}
}

export type PayFieldsConfig = {
	merchantId: string;
	apiKey: string;
	environment: 'test' | 'live';
};

/**
 * Get PayFields client-side configuration (merchant ID, public API key, environment).
 * Used to initialize Payrix PayFields for direct-to-Payrix tokenization in the browser.
 */
export async function getPayFieldsConfig(): Promise<PayFieldsConfig> {
	const result = await makeSecureApiCall('/payrix/payfields-config', 'GET');
	if (!result || result.status >= 400) {
		throw new Error(`Failed to get PayFields config: ${result?.status || 'no response'}`);
	}
	return result.body;
}

/**
 * Get the current boarding status of the merchant.
 *
 * @returns {Promise<PayrixMerchant>}
 */
export async function getMerchant(): Promise<PayrixMerchant> {
	const result = await makeSecureApiCall(
		'/v2/payrix/merchant',
		'GET'
	);

	if (!result || result.status >= 400) return null;
	else return result.body;
}

/**
 * OWA-1112: switch Outlaw Payments on. The server re-reads the merchant and sets the firm's
 * paymentProcessor only when it can take payments now; the updated firm arrives over the socket.
 *
 * @throws {Error} with the server's own message when it did not connect (409, 503)
 */
export async function connect(): Promise<void> {
	const result = await makeSecureApiCall('/payrix/connect', 'POST', {});

	// 409 and 503 carry our own text for the firm (see payrixHandler.ts)
	const message = (result?.body as {message?: string} | undefined)?.message;
	if (result && (result.status === 409 || result.status === 503) && message) throw new Error(message);

	const processed = processResults<void>(result);
	if (processed.isErr()) throw processed.unwrapErr();
}

/**
 * OWA-1112: switch Outlaw Payments off. The server clears the firm's paymentProcessor; the
 * updated firm arrives over the socket.
 *
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function disconnect(): Promise<void> {
	const processed = processResults<void>(await makeSecureApiCall('/payrix/disconnect', 'POST', {}));
	if (processed.isErr()) throw processed.unwrapErr();
}

/**
 * Update information for a contact with Payrix.
 * This is a no-op for Payrix — contact syncing is only used by LawPay.
 */
export async function updateContact(_contact: Contact) {
	// No-op: Payrix does not sync contacts
}

export async function updateTxn(txnId: uuid, description: string): Promise<void> {
    await makeSecureApiCall(`/payrix/txn/${txnId}`, 'PATCH', {description});
}

/**
 * Get the Payrix bank accounts for the current firm.
 *
 * @returns {Promise<BankAccounts>}
 * @throws {Error} The payment server's error when the HTTP request fails.
 */
export async function getAccounts(): Promise<BankAccounts> {
	const result = await makeSecureApiCall('/v2/payrix/accounts', 'GET', {});

	if (!result) return null;
	const processed = processResults<BankAccounts>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Get Payrix contacts for the current firm.
 * No-op: Payrix does not sync contacts. Contact data lives in Outlaw.
 *
 * @returns {Promise<Contact[]>}
 */
export async function getContacts(): Promise<Contact[]> {
	return [];
}

/**
 * Get payment methods for a contact from Payrix.
 *
 * @param {Contact} contact
 * @returns {Promise<PayrixPaymentMethod[]>}
 */
export async function getPaymentMethods(contact: Contact): Promise<PayrixPaymentMethod[]> {
	const result = await makeSecureApiCall('/payrix/contacts/paymentMethods', 'GET', {contactId: contact.id});
	if (!result) return [];

	const {status, body} = result;

	if (status >= 400) {
		if (status === 404) return [];

		// Handle HTTP error
		else throw new Error(`Error ${status}: ${body}`);
	}
	else return body;
}

/**
 * Create a payment method for a contact in Payrix.
 *
 * @param {Contact} contact - the contact to update
 * @param {PayrixPaymentMethod} paymentMethod - the payment method to use
 */
export async function createPaymentMethod(contact: Contact, paymentMethod: PayrixPaymentMethod): Promise<{status: number, body: PayrixToken | {clientMessage: string}}> {
	return makeSecureApiCall(
		`/payrix/contact/paymentMethods`,
		'POST',
		{contact, paymentMethod}
	);
}

/**
 * Update a payment method in Payrix.
 *
 * @param {Contact} _ - not used in this implementation
 * @param {PayrixPaymentMethod} paymentMethod
 * @returns {Promise<PayrixPaymentMethod>}
 */
export async function updatePaymentMethod(_: Contact, paymentMethod: PaymentMethod): Promise<PayrixPaymentMethod> {
	const result = await makeSecureApiCall(
		`/payrix/contact/paymentMethods`,
		'PATCH',
		{paymentMethod}
	);
	if (!result) throw new Error('Unable to connect to payment server');

	const {status, body} = result;

	if (status >= 400) {
		// Handle HTTP error
		throw new Error(`Error ${status}, ${body}`);
	}
	else {
		return body;
	}
}

/**
 * Delete a payment method in Payrix.
 *
 * @param {Contact} _ - not used in this implementation
 * @param {PayrixPaymentMethod} paymentMethod
 * @returns {Promise<void>}
 */
export async function deletePaymentMethod(_: Contact, paymentMethod: PayrixPaymentMethod): Promise<void> {
	const result = await makeSecureApiCall(
		`/payrix/contact/paymentMethods/${paymentMethod.id}`,
		'DELETE',
		{}
	);

	processResults<void>(result);
}

/**
 * Delete a contact in Payrix (NOT in Outlaw).
 * No-op: Payrix does not sync contacts. Contact data lives in Outlaw.
 *
 * @param {string} _processorContactId
 * @returns {Promise<void>}
 */
export async function deleteContact(_processorContactId: string): Promise<void> {
	// No-op: Payrix does not sync contacts
}

/**
 * Retrieves all Payrix customers matching the contact's ID. There can be many.
 *
 * @param {Contact} contact
 * @returns {Promise<PayrixCustomer[]>}
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function getContact(contact: Contact): Promise<PayrixCustomer[]> {
	const result = await makeSecureApiCall(`/payrix/contacts/${contact.processorContactId}`, 'GET', {});
	const processed = processResults<PayrixCustomer[]>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Create a payment using the given payment method for the given amount.
 *
 * @param {string} paymentMethodId - payment method ID from the provider
 * @param {number} amount - amount of the transaction.  Debits will be positive numbers, credits will be negative!
 * @param {string} accountId - the account in which to create the charge
 * @param {Transaction} transaction - related transaction
 * @returns {Promise<*>}
 */
export async function createPayment(paymentMethodId: string, amount: number, accountId: string, transaction: Transaction | Record<string, unknown>): Promise<Result<PayrixTransactionFull, Error>> {
	const result = await makeSecureApiCall(`/payrix/payments`, 'POST', {
		paymentMethodId,
		amount,
		accountId,
		transaction
	});

	const processed = processResults<PayrixTransactionFull>(result);
	// OWA-1454: the payments service's verdicts on a portal charge come before the status
	const code = processed.isErr() ? (processed.unwrapErr() as PaymentsCallError).code : undefined;
	if (code === 'PORTAL_CHARGE_REFUSED') return Err(new PortalChargeRefusedError(processed.unwrapErr().message));
	if (code === 'PORTAL_CHARGE_OPERATOR_REQUIRED') return Err(new PortalChargeOperatorError(processed.unwrapErr().message));
	// A lost answer comes back as a synthetic 500 (makeApiCall), and a 5xx, 408 or 429 may have
	// charged: the charge is sent once and never repeated by the server (OWA-1206).
	if (processed.isErr() && chargeOutcomeUnknown(result?.status)) {
		return Err(new PaymentOutcomeUnknownError(processed.unwrapErr().message, result?.status));
	}
	if (processed.isErr() && chargeNotSent(result?.status)) {
		return Err(new PaymentNotSentError(processed.unwrapErr().message, result?.status));
	}
	return processed;
}

/**
 * Refund (fully) a transaction.
 *
 * @param {string} transactionId
 * @param {number} amount - amount in $.cc
 * @param {string} refundKey - UUID naming this refund. Send the same key when retrying the same refund,
 *  so the server never refunds twice (OWA-1187).
 * @returns {Promise<PayrixTransaction>}
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function refundTransaction(transactionId: string, amount: number, refundKey: string): Promise<PayrixTransaction> {
	const result = await makeSecureApiCall(`/payrix/payments/refund/${transactionId}`, 'POST', {amount, refundKey});

	const processed = processResults<PayrixTransaction>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Void a transaction. On a processed batch the server refunds it instead.
 *
 * @param {string} transactionId
 * @param {string} refundKey - UUID naming the refund this void can become. Send the same key when
 *  retrying the same void (OWA-1187).
 * @returns {Promise<PayrixTransaction>}
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function voidTransaction(transactionId: string, refundKey: string): Promise<PayrixTransaction> {
	const result = await makeSecureApiCall(`/payrix/payments/void/${transactionId}`, 'POST', {refundKey});

	const processed = processResults<PayrixTransaction>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Get full details on a transaction.
 *
 * @param {string} transactionId
 * @returns {Promise<PayrixTransaction>}
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function getTransaction(transactionId: string): Promise<PayrixTransaction> {
	const result = await makeSecureApiCall(`/payrix/payments/${transactionId}`, 'GET', {});

	const processed = processResults<PayrixTransaction>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Get all transactions for the given date range.
 *
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Promise<PayrixTransaction[]>}
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function getTransactions(startDate: Date, endDate: Date): Promise<PayrixTransactionFull[]> {
	const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	const result = await makeSecureApiCall(`/payrix/payments/range`, 'GET', {
		startDate: startDate.toISOString(),
		endDate: endDate.toISOString(),
		timezone
	});

	const processed = processResults<PayrixTransactionFull[]>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Save the application to the server for later use.
 *
 * @param {object} application
 * @returns {Promise<void>}
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function saveApplication(application: CPayrixApplication): Promise<void> {
	const result = await makeSecureApiCall('/payrix/application/save', 'POST', {application});

	const processed = processResults<void>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 *
 * @param {PayrixApplication} application
 */
export async function uploadDocuments(application: CPayrixApplication) {
	const result = await makeSecureApiCall('/payrix/application/upload', 'POST', {application});

	return processResults<void>(result);
}

/**
 * Save the application to the server for later use. Note that this just uses the current firmId on the server
 * side, which is passed over as a default param using makeSecureApiCall.
 */
export async function cancelApplication() {
	const result = await makeSecureApiCall('/payrix/application', 'DELETE');

	return processResults<void>(result);
}

/**
 * Submit the application to Payrix.
 *
 * @param {PayrixApplication} application
 * @returns {Promise<ApplicationSubmissionResults>}
 */
export async function submitApplication(application: CPayrixApplication): Promise<ApplicationSubmissionResults> {
	// OWA-649: `custom` is the firmId tenancy anchor. The server derives it
	// authoritatively from the JWT and ignores any value in the request body, so we
	// do not transmit it — sending a field the server overrides would be a misleading
	// contract. (firmId is still used locally: application validation and the
	// hosted-signup redirect; it just isn't the client's to assert on this write.)
	const applicationBody: Partial<CPayrixApplication> = {...application};
	delete applicationBody.custom;

	const result = await makeSecureApiCall('/payrix/application', 'POST', {application: applicationBody});

	// OWA-920: a firm that already has a Payrix application (OWA-747: one per
	// firm) gets a 409 carrying that application's live status. That is an
	// answer, not a failure: callers show the status, a decline included.
	if (result?.status === 409 && result.body?.applicationStatus) {
		return {applicationStatus: result.body.applicationStatus};
	}

	// Throw the server's error itself; unwrap() would replace it with a generic one.
	const processed = processResults<ApplicationSubmissionResults>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * OWA-679: the current state of this firm's Payrix application, derived
 * server-side from a live read of the merchant. Drives the Home dashboard
 * status card.
 *
 * OWA-745: when Payrix is unreachable the server answers with the last stored
 * status marked `stale`, or an error when nothing is stored. An error rejects
 * here: returning undefined made an outage look exactly like "has not applied".
 *
 * @returns {Promise<PayrixApplicationStatusInfo|PayrixStoredApplicationStatus>}
 * @throws when the lookup fails
 */
export async function getApplicationStatus(): Promise<PayrixApplicationStatusInfo | PayrixStoredApplicationStatus> {
	const result = await makeSecureApiCall('/payrix/application/status', 'GET');

	const processed = processResults<PayrixApplicationStatusInfo | PayrixStoredApplicationStatus>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Get an existing application from the server.
 *
 * @returns {Promise<PayrixApplicationStatus>}
 */
export async function getSavedApplication(): Promise<PayrixApplicationStatus | undefined> {
	const result = await makeSecureApiCall('/payrix/application', 'GET');

	// 404 means no saved application exists — not an error
	if (result?.status === 404) return undefined;

	const processed = processResults<PayrixApplicationStatus>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Get the linkToken to call Plaid.
 *
 * // TODO: Need to figure out the return type
 */
export async function getPlaidToken() {
	const result = await makeSecureApiCall('/payrix/plaidToken', 'GET');
	if (!result) return null;
	else if (result.status === 404) return null;
	else if (result.status >= 400) throw new Error(`${result.status}: ${result.body}`);
	else return result.body;
}

/**
 * Replace a bank account using Plaid instant verification.
 * Deactivates the old account and creates a new one via Plaid.
 *
 * @param accountType "operating" or "iolta"
 * @param publicToken Plaid public token from Plaid Link onSuccess callback
 * @throws {Error} The payment error when the request fails or the server is unavailable.
 */
export async function replaceAccount(accountType: string, publicToken: string): Promise<{created: any; deactivated: any; warning: string | null}> {
	const result = await makeSecureApiCall(
		'/v2/payrix/accounts/replace',
		'POST',
		{accountType, publicToken}
	);

	const processed = processResults<{created: any; deactivated: any; warning: string | null}>(result);
	if (processed.isErr()) throw processed.unwrapErr();
	return processed.unwrap();
}

/**
 * Get the current merchant balance
 *
 * @returns {Promise<PayrixFunds>}
 */
export async function getFunds(): Promise<PayrixFunds> {
	const result = await makeSecureApiCall('/payrix/funds', 'GET');
	if (!result) throw new Error('Could Not Fetch Current Balance');
	if (result.status >= 400) throw new Error(`Error ${result.status}: ${result.body}`);

	return result.body;
}

/**
 * Get date range in yyyy-MM-dd format from a range object.
 *
 * @param {{startDate?: Date, endDate?: Date}} range
 * @returns {{start: string, end: string}}
 */
function getDates(range: {startDate?: Date, endDate?: Date}): { start: string; end: string; } {
	return {
		start: format(range.startDate || new Date(0), 'yyyy-MM-dd'),
		end: format(range.endDate || new Date(), 'yyyy-MM-dd')
	}
}

/**
 * Return the list of chargebacks (disputes)
 * @returns {Promise<ChargebackReturn[]>}
 */
export async function getChargebacks(): Promise<ChargebackReturn[]> {
	const result = await makeSecureApiCall('/payrix/chargebacks', 'GET');
	if (!result) throw new Error('Could Not Fetch Chargebacks');

	else if (result.status >= 400) {
		console.error(`Error ${result.status}`, result.body);

		throw new Error(`Error ${result.status}: ${result.body}`);
	}
	else return result.body;
}

/**
 *
 * @param {PayrixChargebackMessage} message
 * @returns {Promise<PayrixChargebackMessage>}
 */
export async function createChargebackMessage(message: PayrixChargebackMessage): Promise<PayrixChargebackMessage> {
	const result = await makeSecureApiCall('/payrix/chargeback/message', 'POST', message);
	if (!result) throw new Error('Could Not Post Chargeback Message');

	else if (result.status >= 400) throw new Error(`Error ${result.status}: ${result.body}`);
	else return result.body;
}

/**
 * @param {string} messageId
 */
export async function deleteChargebackMessage(messageId: string) {
	const result = await makeSecureApiCall(`/payrix/chargeback/message/${messageId}`, 'DELETE', {});
	if (!result) throw new Error('Could Not Post Chargeback Message');

	else if (result.status >= 400) throw new Error(`Error ${result.status}: ${result.body}`);
	else return result.body;
}

/**
 * Return the list of payouts (withdrawals)
 * @param {{startDate?: Date, endDate?: Date}} range
 * @returns {Promise<PayrixPayoutWithDisbursement[]>}
 */
export async function getPayouts(range: { startDate?: Date; endDate?: Date; }): Promise<PayrixPayoutWithDisbursement[]> {
	const MAX_RETRIES = 3;
	const operation = tabSession.captureGeneration();
	let lastResult: {status: number, body: any} | null = null;

	for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
		operation.assertCurrent();
		const result = await makeSecureApiCall('/payrix/payouts', 'GET', getDates(range));
		operation.assertCurrent();
		lastResult = result;

		if (!result) throw new Error('Could Not Fetch Payouts');

		if (result.status === 503) {
			if (attempt < MAX_RETRIES) {
				console.warn(`Payrix is temporarily unavailable, retrying after 5 seconds (attempt ${attempt + 1}/${MAX_RETRIES})...`);
				await new Promise(resolve => setTimeout(resolve, 5000));
				operation.assertCurrent();
				continue;
			}
			throw new Error('Payrix is temporarily unavailable. Please try again later.');
		}
		else if (result.status >= 400) throw new Error(`Error ${result.status} ${result.body || ''}`);
		else return result.body;
	}

	throw new Error('Could Not Fetch Payouts');
}

/**
 * Return the list of payouts (withdrawals)
 * @param {string[]} txnIds
 * @returns {Promise<PayrixFeeSummary[]>}
 */
export async function getTxnFees(txnIds: string[]): Promise<PayrixFeeSummary[]> {
	if (!txnIds || txnIds.length === 0) {
		return [];
	}

	const result = await makeSecureApiCall('/payrix/txn/fees', 'GET', {txnIds});

	if (!result) throw new Error('Could Not Fetch Transaction Fees');
	else if (result.status >= 400) throw new Error(`Error ${result.status}: ${JSON.stringify(result.body, null, 2)}`);
	else return result.body;
}

/**
 * Get a list of entries, sorted desc by created date, for a given date range. Returns an array of PayrixEntryEnhanced.
 *
 * @param {Date} startDate
 * @param {Date} endDate
 */
export async function getEntriesByRange(startDate: Date, endDate: Date): Promise<Result<Partial<PayrixEntryEnhanced>[], HttpError>> {
	if (!startDate || !endDate) return Err(new HttpError(400, `Invalid date range: ${startDate} - ${endDate}`));
	const result = await makeSecureApiCall('/payrix/entries/range', 'GET', {startDate: startDate.toISOString(), endDate: endDate.toISOString()});
    if (!result) return Err(new HttpError(503, 'Could Not Fetch Transaction Fees By Date, server is busy'));
    else if (result.status >= 400) return Err(new HttpError(result.status, httpErrorMessage(result.status, result.body)));
    else return Ok(result.body || []);
}

export async function getTxnResults(txnId: string) : Promise<Result<PayrixTxnResult[], HttpError>> {
	const result = await makeSecureApiCall('/payrix/txn/results', 'GET', {txnId});
	if (!result) return Err(new HttpError(503, 'Could Not Fetch Transaction By Date Range, server is busy'));
	else if (result.status >= 400) return Err(new HttpError(result.status, httpErrorMessage(result.status, result.body)));
	else return Ok(result.body || []);
}

/**
 * Get a list of transactions, sorted desc by created date, for a given date range. Returns an array of PayrixTransactionWithPayment.
 *
 * @param {Date} startDate
 * @param {Date} endDate
 */
export async function getTxnsByRange(startDate: Date, endDate: Date): Promise<Result<PayrixTransactionWithPayment[], HttpError>> {
	if (!startDate || !endDate) return Err(new HttpError(400, `Invalid date range: ${startDate} - ${endDate}`));
	const result = await makeSecureApiCall('/payrix/txns/range', 'GET', {startDate: startDate.toISOString(), endDate: endDate.toISOString()});
    if (!result) return Err(new HttpError(503, 'Could Not Fetch Transaction By Date Range, server is busy'));
    else if (result.status >= 400) return Err(new HttpError(result.status, httpErrorMessage(result.status, result.body)));
	else return Ok(result.body || []);
}

/**
 * Get payouts that failed, no time constraints.
 *
 * @returns {Promise<string[]>}
 */
export async function getFailedPayouts(): Promise<string[]> {
	const result = await makeSecureApiCall('/payrix/failedPayouts', 'GET');
	if (!result) throw new Error('Could Not Fetch Failed Payouts');

	if (result.status >= 400) throw new Error(`Error ${result.status}: ${result.body}`);
	else return result.body;
}

/**
 * Get extensive details on a payout, including transactions related to the payout, and disbursements.
 *
 * @param {string} disbursementId
 * @returns {Promise<PayoutDetails>}
 */
export async function getPayoutDetails(disbursementId: string): Promise<PayoutDetails> {
	const result = await makeSecureApiCall('/payrix/payout/details', 'GET', {disbursementId});
	if (!result) throw new Error('Could Not Fetch Current Payouts');
	else if (result.status >= 400) throw new Error(`Error ${result.status}: ${result.body}`);
	else return result.body;
}

/**
 * Retry a failed payout.
 *
 * @param {string} payoutId - ID of the payout to retry
 * @returns {Promise<PayrixPayout>}
 */
export async function retryPayout(payoutId: string): Promise<PayrixPayout> {
	const result = await makeSecureApiCall('/payrix/retryPayout', 'POST', {payoutId});
	if (!result) throw new Error('Could Not Fetch Current Payouts');
	else if (result.status === 404) throw new Error(`Could not find this payout in the system. Please contact Support.`);
	else if (result.status >= 400) throw new Error(`${result.status} - ${result.body}`);
	else return result.body;
}

/**
 *
 * @param {Transaction} tr
 * @returns {Promise<string>}
 */
export async function getUserWhoProcessedTransaction(tr: Transaction): Promise<string> {
	const entry = (await getEntityHistory<Transaction>('transaction', tr))
		.sort((a, b) => a.addedOn- b.addedOn)
		.map(transaction => {
			return {
				asOf: transaction.addedOn,
				addedBy: transaction.addedBy,
				id: transaction.processorPaymentMeta?.id,
				meta: transaction.processorPaymentMeta
			};
		})
		.filter(h => !!h.id)[0];

	if (entry) {
		const {addedBy, meta} = entry;
		const contact = getContactFromUserId(addedBy);
		const userName = contact ? formatFML(contact) : 'Unknown';
		const date = meta.chargeDate ? meta.chargeDate.toLocaleString('en-US') : null;
		if (date) return `${userName} on ${date}`;
		else return userName;
	}
	else return null;
}
